import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, BadgeCheck, CheckCircle2, Loader2, PackageCheck, Warehouse, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { inputCls } from "../Login";
import ShipmentDocs from "../../components/ShipmentDocs";
import BoxFields from "../../components/BoxFields";

const STEPS = ["Destination Check", "To Address & Package", "Courier & Price", "Booked"];

export default function AgentBooking({ isPickup = false }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [warehouse, setWarehouse] = useState(null);
  const [toPincode, setToPincode] = useState("");
  const [serviceable, setServiceable] = useState(null);
  const [to, setTo] = useState({});
  const [pkg, setPkg] = useState({ weight: "", length: "", width: "", height: "", pieces: 1, description: "" });
  const [paymentType, setPaymentType] = useState("PREPAID");
  const [orderAmount, setOrderAmount] = useState("");
  const [rates, setRates] = useState(null);
  const [boxInfo, setBoxInfo] = useState({ package_type: "SPS", boxes: null });
  const [selected, setSelected] = useState(null);
  const [booked, setBooked] = useState(null);
  const [busy, setBusy] = useState(false);
  const idemKey = useRef(crypto.randomUUID());

  useEffect(() => {
    api.get("/agent/warehouse").then((r) => setWarehouse(r.data))
      .catch((e) => toast.error(apiError(e)));
  }, []);

  const checkServiceability = async (e) => {
    e.preventDefault();
    setBusy(true);
    setServiceable(null);
    try {
      const res = await api.post("/rates/serviceability", {
        pickup_pincode: warehouse.pincode, delivery_pincode: toPincode });
      setServiceable(res.data);
      if (res.data.serviceable) {
        toast.success("Lane is serviceable — partners available!");
        api.get(`/public/pincode/${toPincode}`).then((r) => {
          if (r.data.city || r.data.state) {
            setTo((prev) => ({ ...prev, city: r.data.city || prev.city, state: r.data.state || prev.state }));
            toast.info(`Detected: ${r.data.city}, ${r.data.state}`);
          }
        }).catch(() => {});
      } else toast.error(res.data.message || "Not serviceable on this lane");
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const fetchRates = async () => {
    setBusy(true);
    setRates(null);
    try {
      const res = await api.post("/rates/calculate", {
        pickup_pincode: warehouse.pincode, delivery_pincode: toPincode,
        weight: parseFloat(pkg.weight) || 0.5,
        length: parseFloat(pkg.length) || 0, width: parseFloat(pkg.width) || 0, height: parseFloat(pkg.height) || 0,
        pieces: parseInt(pkg.pieces) || 1, payment_type: paymentType,
        order_amount: parseFloat(orderAmount) || 0,
        cod_amount: paymentType === "COD" ? parseFloat(orderAmount) || 0 : null,
        ...(boxInfo.boxes ? { boxes: boxInfo.boxes } : {}),
      });
      setRates(res.data);
      if (!res.data.rates.length) toast.error("No courier partners on this lane");
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const book = async () => {
    if (!selected || busy) return;
    setBusy(true);
    try {
      const res = await api.post("/shipments", {
        pickup: warehouse, delivery: { ...to, pincode: toPincode },
        package: { ...pkg, weight: parseFloat(pkg.weight) || 0.5, pieces: parseInt(pkg.pieces) || 1 },
        boxes: boxInfo.boxes || undefined,
        payment_type: paymentType, order_amount: parseFloat(orderAmount) || 0,
        cod_amount: paymentType === "COD" ? parseFloat(orderAmount) || 0 : null,
        courier_id: selected.courier_id, courier_name: selected.courier_name,
        service: selected.service, base_price: selected.base_price, edd: selected.edd,
        pay_via_wallet: true, idempotency_key: idemKey.current,
      });
      idemKey.current = crypto.randomUUID();
      setBooked(res.data);
      setStep(3);
      toast.success(`Shipment booked · AWB ${res.data.awb}`);
      if (isPickup) {
        try { navigator.clipboard?.writeText(res.data.awb); } catch { /* clipboard optional */ }
        toast.success("AWB copied — returning to your pickup panel…");
        setTimeout(() => navigate("/pickup", { state: { bookedAwb: res.data.awb } }), 2500);
      }
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  if (!warehouse) return <p className="py-20 text-center text-slate-400" data-testid="warehouse-loading">Loading your warehouse profile…</p>;

  const setT = (k) => (e) => setTo({ ...to, [k]: e.target.value });

  return (
    <div data-testid="agent-booking-wizard" className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-navy sm:text-3xl">Agent Booking Desk</h1>
        <p className="mt-1 text-sm text-slate-500">Live booking from your registered INDIAFAST warehouse</p>
      </div>

      {/* step indicator */}
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s} className="flex flex-1 items-center gap-2">
            <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-bold ${i <= step ? "bg-saffron text-white" : "bg-slate-200 text-slate-500"}`}>{i + 1}</span>
            <span className={`hidden text-xs font-semibold sm:block ${i <= step ? "text-navy" : "text-slate-400"}`}>{s}</span>
            {i < STEPS.length - 1 && <span className={`h-px flex-1 ${i < step ? "bg-saffron" : "bg-slate-200"}`} />}
          </div>
        ))}
      </div>

      {/* from warehouse card */}
      <div className="flex items-start gap-4 rounded-2xl border border-saffron/30 bg-saffron-light p-5" data-testid="from-warehouse-card">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-saffron text-white"><Warehouse className="h-5 w-5" /></span>
        <div className="text-sm">
          <p className="font-heading font-bold text-navy">From: Your Warehouse <span className="font-mono text-xs text-saffron">#{warehouse.warehouse_id}</span></p>
          <p className="mt-0.5 text-slate-600">{warehouse.name} · {warehouse.address}, {warehouse.city}, {warehouse.state} — <span className="font-mono font-semibold">{warehouse.pincode}</span> · {warehouse.phone}</p>
        </div>
      </div>

      {step === 0 && (
        <form onSubmit={checkServiceability} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="agent-step-1">
          <h3 className="font-heading text-lg font-bold text-navy">Where is this shipment going?</h3>
          <p className="mt-1 text-xs text-slate-500">We validate both pincodes live on the INDIAFAST network and check partner availability.</p>
          <div className="mt-4 flex gap-3">
            <input required maxLength={6} data-testid="agent-to-pincode-input" className={`${inputCls} max-w-xs`}
                   placeholder="Destination pincode e.g. 411005" value={toPincode}
                   onChange={(e) => { setToPincode(e.target.value); setServiceable(null); }} />
            <button type="submit" disabled={busy} data-testid="agent-check-btn"
                    className="flex items-center gap-2 rounded-lg bg-navy px-5 py-2.5 text-sm font-bold text-white hover:bg-saffron disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Check
            </button>
          </div>
          {serviceable && (
            <div data-testid="serviceability-result"
                 className={`mt-4 flex items-center gap-3 rounded-xl p-4 ${serviceable.serviceable ? "bg-emerald-50" : "bg-red-50"}`}>
              {serviceable.serviceable
                ? <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                : <XCircle className="h-5 w-5 text-red-500" />}
              <div className="text-sm">
                <p className={`font-bold ${serviceable.serviceable ? "text-emerald-700" : "text-red-600"}`}>
                  {serviceable.serviceable ? "Serviceable — courier partners available" : "Not serviceable"}
                </p>
                <p className="text-xs text-slate-500">{warehouse.pincode} → {toPincode} · verified live on INDIAFAST network{serviceable.partners_available ? ` · ${serviceable.partners_available} partner options` : ""}</p>
              </div>
              {serviceable.serviceable && (
                <button type="button" onClick={() => setStep(1)} data-testid="agent-next-btn"
                        className="ml-auto flex items-center gap-1.5 rounded-full bg-saffron px-4 py-2 text-xs font-bold text-white hover:bg-saffron-hover">
                  Continue <ArrowRight className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}
        </form>
      )}

      {step === 1 && (
        <div className="space-y-6" data-testid="agent-step-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="mb-4 font-heading text-lg font-bold text-navy">To Address</h3>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Recipient Name</label>
                <input required className={inputCls} data-testid="agent-to-name" value={to.name || ""} onChange={setT("name")} /></div>
              <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Phone</label>
                <input required className={inputCls} data-testid="agent-to-phone" value={to.phone || ""} onChange={setT("phone")} /></div>
              <div className="col-span-2"><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Address</label>
                <input required className={inputCls} data-testid="agent-to-address" value={to.address || ""} onChange={setT("address")} /></div>
              <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">City</label>
                <input required className={inputCls} data-testid="agent-to-city" value={to.city || ""} onChange={setT("city")} /></div>
              <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">State</label>
                <input required className={inputCls} data-testid="agent-to-state" value={to.state || ""} onChange={setT("state")} /></div>
              <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Pincode (validated)</label>
                <input readOnly className={`${inputCls} bg-emerald-50 font-mono font-bold text-emerald-700`} data-testid="agent-to-pincode" value={toPincode} /></div>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="mb-4 font-heading text-lg font-bold text-navy">Package & Payment</h3>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Weight (KG)</label>
                <input required type="number" step="0.1" min="0.1" className={inputCls} data-testid="agent-pkg-weight" value={pkg.weight} onChange={(e) => setPkg({ ...pkg, weight: e.target.value })} /></div>
              {["length", "width", "height"].map((d) => (
                <div key={d}><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">{d[0].toUpperCase()} (cm)</label>
                  <input type="number" min="0" className={inputCls} data-testid={`agent-pkg-${d}`} value={pkg[d]} onChange={(e) => setPkg({ ...pkg, [d]: e.target.value })} /></div>
              ))}
              <div className="col-span-2"><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Contents</label>
                <input className={inputCls} data-testid="agent-pkg-desc" placeholder="e.g. Books, garments" value={pkg.description} onChange={(e) => setPkg({ ...pkg, description: e.target.value })} /></div>
              <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Payment</label>
                <select className={inputCls} data-testid="agent-payment-type" value={paymentType} onChange={(e) => setPaymentType(e.target.value)}>
                  <option value="PREPAID">Prepaid</option><option value="COD">COD</option>
                </select></div>
              <div><label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Order Value ₹</label>
                <input type="number" min="0" className={inputCls} data-testid="agent-order-amount" value={orderAmount} onChange={(e) => setOrderAmount(e.target.value)} /></div>
            </div>
            <BoxFields totalWeight={pkg.weight} onChange={setBoxInfo} />
          </div>
          <div className="flex justify-between">
            <button onClick={() => setStep(0)} className="flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-navy"><ArrowLeft className="h-4 w-4" /> Back</button>
            <button onClick={() => { setStep(2); fetchRates(); }} disabled={!to.name || !to.phone || !to.address || !to.city || !to.state || !pkg.weight}
                    data-testid="agent-step2-next-btn"
                    className="flex items-center gap-2 rounded-full bg-saffron px-6 py-2.5 text-sm font-bold text-white hover:bg-saffron-hover disabled:opacity-50">
              Get Partner Prices <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="agent-step-3">
          <h3 className="font-heading text-lg font-bold text-navy">All Partner Prices — {warehouse.pincode} → {toPincode}</h3>
          <p className="mt-1 text-xs text-slate-500">Live partner rates · chargeable {rates?.chargeable_weight || "…"} kg · inclusive of 18% GST</p>
          {!rates ? (
            <p className="flex items-center gap-2 py-10 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Fetching live prices from all courier partners…</p>
          ) : (
            <div className="mt-5 grid gap-3 sm:grid-cols-2" data-testid="agent-rates">
              {rates.rates.map((r, i) => (
                <button key={i} type="button" onClick={() => setSelected(r)} data-testid={`agent-rate-card-${i}`}
                        className={`flex items-center justify-between gap-3 rounded-xl border-2 p-4 text-left transition-all ${selected === r ? "border-saffron bg-saffron-light" : "border-slate-200 hover:border-saffron/50"}`}>
                  <div className="flex min-w-0 items-center gap-3">
                    {r.image ? <img src={r.image} alt="" className="h-9 w-9 shrink-0 rounded-md bg-slate-50 object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : null}
                    <div className="min-w-0">
                      <p className="truncate font-heading text-sm font-bold text-navy">{r.courier_name}</p>
                      <p className="text-xs text-slate-500">{r.edd || "Standard"} {r.auto_pickup === "YES" ? "· auto pickup" : ""}</p>
                    </div>
                  </div>
                  <p className="shrink-0 font-heading text-xl font-extrabold text-navy">₹{r.customer_price.toLocaleString("en-IN")}</p>
                </button>
              ))}
            </div>
          )}
          <div className="mt-6 flex justify-between">
            <button onClick={() => setStep(1)} className="flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-navy"><ArrowLeft className="h-4 w-4" /> Back</button>
            <button onClick={book} disabled={!selected || busy} data-testid="agent-confirm-btn"
                    className="flex items-center gap-2 rounded-full bg-saffron px-6 py-2.5 text-sm font-bold text-white shadow-lg shadow-saffron/30 hover:bg-saffron-hover disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <BadgeCheck className="h-4 w-4" />}
              {selected ? `Book ${selected.courier_name} — ₹${selected.customer_price.toLocaleString("en-IN")}` : "Select a partner"}
            </button>
          </div>
        </div>
      )}

      {step === 3 && booked && (
        <div className="space-y-6" data-testid="agent-step-4">
          <div className="rounded-2xl border border-emerald-200 bg-white p-8 text-center" data-testid="agent-success">
            <PackageCheck className="mx-auto h-14 w-14 text-emerald-500" />
            <h2 className="mt-4 font-heading text-2xl font-bold text-navy">Booking Confirmed</h2>
            <p className="mt-2 text-sm text-slate-500">Courier assigned & pickup scheduled from your warehouse</p>
            <p className="mt-3 font-mono text-lg font-bold text-saffron" data-testid="agent-awb">AWB {booked.awb}</p>
            <p className="font-mono text-xs text-slate-400">{booked.order_id} · {booked.invoice_number}</p>
            {isPickup ? (
              <p className="mt-2 text-sm font-semibold text-navy">Booking-only pickup booking — bill settles in office. Returning to your panel…</p>
            ) : (
              <p className="mt-2 text-sm font-semibold text-navy">Your commission (30% of invoice value) has been accrued automatically.</p>
            )}
          </div>
          <ShipmentDocs shipment={booked} />
          <div className="flex justify-center gap-3">
            <button onClick={() => navigate(`/track/${booked.awb}`)} data-testid="agent-track-btn"
                    className="rounded-full bg-navy px-5 py-2.5 text-sm font-bold text-white hover:bg-saffron">Track</button>
            <button onClick={() => { setStep(0); setTo({}); setPkg({ weight: "", length: "", width: "", height: "", pieces: 1, description: "" }); setToPincode(""); setServiceable(null); setRates(null); setSelected(null); setBooked(null); }}
                    data-testid="agent-new-booking-btn"
                    className="rounded-full border border-slate-300 px-5 py-2.5 text-sm font-bold text-navy hover:bg-slate-100">New Booking</button>
          </div>
        </div>
      )}
    </div>
  );
}
